import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { AGENT_BAR_SCENARIOS, agentBarScenarioAt, type AgentBarScenarioGroup } from './agentBarScenarios';
import {
  feedbackVisibleForScenario,
  formatAgentBarFeedback,
  readAgentBarFeedback,
  saveAgentBarFeedback,
  type AgentBarFeedbackComment,
} from './agentBarFeedback';

const SCENARIO_GROUPS: AgentBarScenarioGroup[] = ['Core flow', 'Questions', 'Reviews', 'Outcomes'];

function feedbackId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `agent-bar-feedback-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function loadFeedback(): AgentBarFeedbackComment[] {
  try {
    return readAgentBarFeedback(window.localStorage);
  } catch {
    return [];
  }
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export function AgentBarStateLab({
  previewIndex,
  onPreviewIndexChange,
  previewAction,
  onResetPreview,
}: {
  previewIndex: number | null;
  onPreviewIndexChange: (index: number | null) => void;
  previewAction: string | null;
  onResetPreview: () => void;
}): ReactElement {
  const scenario = agentBarScenarioAt(previewIndex);
  const [comments, setComments] = useState<AgentBarFeedbackComment[]>(loadFeedback);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState<'global' | 'state'>(scenario ? 'state' : 'global');
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    try {
      saveAgentBarFeedback(window.localStorage, comments);
    } catch {
      // Feedback still works for this session when storage is unavailable.
    }
  }, [comments]);

  useEffect(() => {
    if (!scenario) setScope('global');
  }, [scenario]);

  const visibleComments = useMemo(
    () => feedbackVisibleForScenario(comments, scenario?.id ?? null),
    [comments, scenario?.id],
  );

  const step = (direction: -1 | 1) => {
    const count = AGENT_BAR_SCENARIOS.length;
    const current = previewIndex ?? (direction === 1 ? -1 : 0);
    onPreviewIndexChange((current + direction + count) % count);
  };

  const addComment = () => {
    const body = draft.trim();
    if (!body) return;
    setComments((current) => [
      ...current,
      {
        id: feedbackId(),
        scope: scope === 'state' && scenario ? scenario.id : 'global',
        body,
        createdAt: new Date().toISOString(),
      },
    ]);
    setDraft('');
  };

  const copyReview = async () => {
    try {
      await copyText(formatAgentBarFeedback(comments, AGENT_BAR_SCENARIOS));
      setCopyStatus('Copied');
    } catch {
      setCopyStatus('Copy failed');
    }
    window.setTimeout(() => setCopyStatus(''), 1800);
  };

  return (
    <div className="agent-bar-state-lab">
      <section className="message-state-lab" aria-label="AgentBar state simulator">
        <div className="message-state-lab-head">
          <div>
            <span className="thinking-aura-eyebrow">STATE PLAYBACK</span>
            <strong>{scenario ? `${previewIndex! + 1} / ${AGENT_BAR_SCENARIOS.length}` : 'Live data'}</strong>
          </div>
          {scenario ? <button type="button" onClick={() => onPreviewIndexChange(null)}>Exit preview</button> : null}
        </div>
        <div className="message-state-lab-controls">
          <button type="button" onClick={() => step(-1)} aria-label="Previous AgentBar state">‹</button>
          <select
            value={previewIndex === null ? 'live' : String(previewIndex)}
            onChange={(event) => onPreviewIndexChange(event.target.value === 'live' ? null : Number(event.target.value))}
            aria-label="Preview AgentBar state"
          >
            <option value="live">Live AgentBar data</option>
            {SCENARIO_GROUPS.map((group) => (
              <optgroup key={group} label={group}>
                {AGENT_BAR_SCENARIOS.map((item, index) => item.group === group ? (
                  <option key={item.id} value={index}>{item.label}</option>
                ) : null)}
              </optgroup>
            ))}
          </select>
          <button type="button" onClick={() => step(1)} aria-label="Next AgentBar state">›</button>
        </div>
        <div className="message-state-lab-summary" aria-live="polite">
          <strong>{scenario?.label ?? 'Live AgentBar'}</strong>
          <p>{scenario?.description ?? 'Choose a state to pause live task data and drive the real AgentBar component.'}</p>
        </div>
        {scenario ? (
          <div className="message-state-lab-result" aria-live="polite">
            <div>
              <span>Interaction result</span>
              <p>{previewAction ?? 'Use any visible AgentBar control to run its current platform behavior.'}</p>
            </div>
            <button type="button" onClick={onResetPreview}>Reset state</button>
          </div>
        ) : null}
      </section>

      <section className="agent-bar-feedback" aria-label="AgentBar feedback">
        <div className="agent-bar-feedback-head">
          <div>
            <span className="thinking-aura-eyebrow">REVIEW NOTES</span>
            <h4>Comments</h4>
          </div>
          <button type="button" onClick={copyReview} disabled={comments.length === 0}>
            {copyStatus || `Copy all (${comments.length})`}
          </button>
        </div>

        <div className="agent-bar-feedback-scope" role="group" aria-label="Comment scope">
          <button type="button" aria-pressed={scope === 'global'} onClick={() => setScope('global')}>Global fix</button>
          <button
            type="button"
            aria-pressed={scope === 'state'}
            disabled={!scenario}
            onClick={() => setScope('state')}
          >
            This state{scenario ? `: ${scenario.label}` : ''}
          </button>
        </div>

        <label className="agent-bar-feedback-composer">
          <span>{scope === 'global' ? 'Applies across the AgentBar' : `Applies only to ${scenario?.label ?? 'this state'}`}</span>
          <textarea
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') addComment();
            }}
            placeholder="Describe the fix or adjustment..."
          />
        </label>
        <div className="agent-bar-feedback-actions">
          <span>⌘ Enter to add</span>
          <button type="button" onClick={addComment} disabled={!draft.trim()}>Add comment</button>
        </div>

        <div className="agent-bar-feedback-list" aria-live="polite">
          {visibleComments.length === 0 ? (
            <p className="agent-bar-feedback-empty">No global or state-specific comments here yet.</p>
          ) : visibleComments.map((comment) => (
            <article key={comment.id} className="agent-bar-feedback-comment">
              <div>
                <span>{comment.scope === 'global' ? 'Global' : scenario?.label ?? 'State'}</span>
                <button
                  type="button"
                  onClick={() => setComments((current) => current.filter((item) => item.id !== comment.id))}
                  aria-label="Delete comment"
                >
                  Delete
                </button>
              </div>
              <p>{comment.body}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
